// Rank, on the client. Pure functions over GET /api/me's `products` array, in
// their own module so they can be tested under node --test — there is no DOM test
// harness in this repository, and web/src/components/tickets/queue-query.js is the
// precedent for putting the logic where it can be reached.
//
// THESE DECIDE WHAT IS SHOWN, NEVER WHAT IS ALLOWED. src/middleware/auth.js's
// `requireProductRole` is the enforcement and refuses regardless of what this
// says. SPEC.md §7.3: "hiding a button is presentation, never authorization" —
// and src/services/tickets.js:901-903 records a case where UI-only enforcement
// had to be moved server-side.
//
// NOT `user.role`. That field is documented display-only on GET /api/me and is a
// trigger-maintained projection of the SUPPORT row only, so reading it is right
// by accident today and wrong in both directions once a second product exists.
// `products` carries (product, role) pairs; ask it.

/** Ordered least to most permissive. Mirrors src/auth/role-map.js:22-28 exactly. */
export const ROLE_RANK = {
  viewer: 0,
  collaborator: 1,
  agent: 2,
  lead: 3,
  admin: 4,
};

/**
 * The role this user holds in `product`, or null if they hold none.
 * `products: []` means authenticated and entitled to nothing.
 *
 * @param {{products?: Array<{product: string, role: string}>}} user
 * @param {string} product
 * @returns {string|null}
 */
export function productRole(user, product) {
  const entry = (user?.products ?? []).find((p) => p.product === product);
  return entry ? entry.role : null;
}

/**
 * True when the user holds at least `minRole` in `product`.
 *
 * Fails CLOSED on anything it does not recognise — an unknown held role and an
 * unknown bar both answer false. A permissive fallback here would show a section
 * whose every call then 403s, which reads as a broken app rather than as a
 * permissions boundary.
 *
 * The two `undefined` guards below are belt and braces, and MEASURED to be so:
 * deleting both changes no answer, because `undefined >= 3` and `4 >= undefined`
 * are already false. They are kept because the next edit — a `?? 0` default, a
 * `Number()`, a lookup that answers 0 for a miss — makes them load-bearing, and
 * because "fails closed" should be readable rather than inferred from NaN
 * semantics. The tests that name it ("an unknown role fails closed") bite an
 * implementation that fails OPEN, not the removal of these two lines.
 *
 * @param {object} user
 * @param {string} product
 * @param {'viewer'|'collaborator'|'agent'|'lead'|'admin'} minRole
 * @returns {boolean}
 */
export function atLeastRole(user, product, minRole) {
  const required = ROLE_RANK[minRole];
  if (required === undefined) return false;
  const held = ROLE_RANK[productRole(user, product)];
  if (held === undefined) return false;
  return held >= required;
}
